import { useEffect, useState } from 'react';

import { Box, CircularProgress, Grid, Typography } from '@material-ui/core';
import { format, parseISO } from 'date-fns';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { ComingSoon } from 'src/components/ComingSoon';
import Section from 'src/components/ProfileSection';

import { TableInstitutionRanking } from './Table';
import { useInstitutionRankingStyles } from './style';
import { Paywall } from '../../../components/Paywall';
import RankingFilters from '../../../components/RankingFilters';
import MarketsNavigation from '../../../components/UI/MarketsNavigation/MarketsNavigation';
import { useAuthContext } from '../../../context/AuthContextProvider';
import {
  findAllFilters,
  findFilters,
  findRankItemHistory,
  getFirstYearFromFilters,
  getRankingHasConsolidated,
} from '../../../services/ranking';

const ContentRanking = ({
  markets,
  state,
  setState,
  classes,
  filters,
  allFilters,
  hasConsolidated,
  hashId,
  loadingFilters,
}) => {
  const isFiagroMarket = state.market === 'FIAGRO';
  const hasFiagroFilters = !!filters?.FIAGRO;

  const { data, isLoading: loading } = useQuery(
    ['rank-items', state, filters],
    () =>
      findRankItemHistory({
        institutionId: hashId,
        market: state.market,
        type: state.ranking,
        timeRange: state.timeRange,
        criteria: state.criteria,
      }),
    { enabled: !loadingFilters && (!isFiagroMarket || hasFiagroFilters) },
  );

  if (loadingFilters || loading) return <CircularProgress color="secondary" />;

  if (isFiagroMarket && !hasFiagroFilters) {
    return (
      <ComingSoon
        message="Os dados de rankings FIAGRO para esta instituição estarão disponíveis em breve."
        iconSize="small"
      />
    );
  }

  if (!data) return <CircularProgress color="secondary" />;

  return (
    <>
      <Grid container style={{ marginTop: '2rem' }}>
        <Grid item xs={12} md={9}>
          <RankingFilters
            state={state}
            setState={setState}
            classes={classes}
            rankings={filters && filters[state.market]?.rankings}
            filters={allFilters}
            hasConsolidated={hasConsolidated}
            hasCriteria
            typeRank="ranking_institution"
            institutionId={hashId}
          />
        </Grid>
        {data?.length ? (
          <Grid item xs={12} md={3} className={classes.gridDate}>
            <Typography className="updated">{`Atualizado em ${format(
              parseISO(data[0]?.updatedAt),
              'dd/MM/yyyy',
            )}`}</Typography>
            <Typography className="baseMonth">
              {data &&
                `Mês base ${format(
                  new Date(
                    data[0]?.rank?.baseYear,
                    data[0]?.rank?.baseMonth - 1,
                  ),
                  'MM/yyyy',
                )}`}
            </Typography>
          </Grid>
        ) : (
          ''
        )}
      </Grid>

      <Box>
        <Typography color="primary" className={classes.title}>
          Histórico
        </Typography>
        <TableInstitutionRanking
          classes={classes}
          state={state}
          data={data}
          loading={loading}
          valueLabel={data[0]?.criteria?.label}
          month={data[0]?.rank?.baseMonth}
        />
      </Box>
    </>
  );
};

const INITIAL_STATE = {
  market: 'FIDC',
};

function getFirstAvailableMarket(data) {
  const markets = ['CRI', 'CRA', 'FII', 'FIDC', 'FIAGRO'];
  return markets?.filter(
    (marketName) => data[marketName]?.rankings?.length > 0,
  )[0];
}

export default function InstitutionRankings({ linkState }) {
  const classes = useInstitutionRankingStyles();
  const { hashId } = useParams();
  const [state, setState] = useState(linkState?.state ?? INITIAL_STATE);
  const { auth, upgradedPlanByPermission } = useAuthContext();

  const hasPermission =
    auth?.user?.subscription?.plan?.permissions['instituicao_rankings'];

  const { data, isLoading: loading } = useQuery(
    'filters',
    () => findFilters(hashId, auth.isLoggedIn),
    {
      onSuccess: (filtersData) => {
        if (!state.year) {
          setState({
            ...state,
            year: getFirstYearFromFilters(filtersData, state.market),
          });
        }
      },
      enabled: hasPermission,
    },
  );

  const { data: allFilters, isLoading: isLoadingAllFilters } = useQuery(
    ['allFilters', hashId],
    () => findAllFilters(hashId, auth.isLoggedIn),
    { enabled: hasPermission },
  );

  useEffect(() => {
    if (data) {
      const firstAvailableMarket = getFirstAvailableMarket(data);

      if (firstAvailableMarket && !linkState) {
        const firstRanking = data[firstAvailableMarket].rankings[0];
        setState({
          ...state,
          market: firstAvailableMarket,
          ranking: firstRanking?.type ?? firstRanking?.code,
          criteria: firstRanking?.criterias?.[0]?.code,
          year: getFirstYearFromFilters(data, firstAvailableMarket),
          timeRange:
            firstAvailableMarket === 'FIAGRO' ? 'consolidated' : 'yearly',
        });
      }
    }
  }, [data]);

  const markets = data && Object.keys(data);

  const hasConsolidated =
    data && getRankingHasConsolidated(data, state.market, state.ranking);

  const handleChangeMarket = (value) => {
    const firstRanking = data?.[value]?.rankings?.[0];
    setState({
      ...state,
      market: value,
      ranking: firstRanking?.type ?? firstRanking?.code,
      criteria: firstRanking?.criterias?.[0]?.code,
      year: getFirstYearFromFilters(data, value),
      timeRange: value === 'FIAGRO' ? 'consolidated' : 'yearly',
    });
  };

  return (
    <Section>
      <Paywall
        allow={
          auth?.user?.subscription?.plan?.permissions['instituicao_rankings']
        }
        content={`Assine o plano ${upgradedPlanByPermission(
          'instituicao_rankings',
        )} e tenha acesso a essa aba.`}>
        <MarketsNavigation
          value={state.market}
          handleSelectMarket={handleChangeMarket}
          filters={data}
          markets={['FIDC', 'FII', 'CRI', 'CRA', 'FIAGRO']}
        />
        <ContentRanking
          classes={classes}
          state={state}
          setState={(partial) => setState((prev) => ({ ...prev, ...partial }))}
          markets={markets}
          hasConsolidated={hasConsolidated}
          hashId={hashId}
          filters={data}
          allFilters={allFilters}
          loadingFilters={loading || isLoadingAllFilters}
        />
      </Paywall>
    </Section>
  );
}
